import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  #drawerScrollY = 0;
  /** @type {ScrollRestoration | null} */
  #previousScrollRestoration = null;
  #closingFromHistory = false;

  /** @type {ResizeObserver | null} */
  #summaryResizeObserver = null;
  /** @type {MutationObserver | null} */
  #summaryMutationObserver = null;
  /** @type {Element | null} */
  #observedSummary = null;
  /** @type {number | null} */
  #stickyFrame = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#startStickyObservation);
    this.addEventListener(DialogCloseEvent.eventName, this.#stopStickyObservation);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#startStickyObservation);
    this.removeEventListener(DialogCloseEvent.eventName, this.#stopStickyObservation);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#stopStickyObservation();
    if (this.#previousScrollRestoration !== null) {
      history.scrollRestoration = this.#previousScrollRestoration;
      this.#previousScrollRestoration = null;
    }
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    this.#drawerScrollY = Math.abs(parseFloat(document.body.style.top)) || 0;
    this.#previousScrollRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    if (!history.state?.cartDrawerOpen) {
      history.pushState({ ...history.state, cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    if (!this.#closingFromHistory && history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    this.#closingFromHistory = true;
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
    // The browser otherwise restores the underlying entry's old scroll position
    // after dialog.close(), undoing the dialog's own scroll restoration.
    requestAnimationFrame(() => {
      window.scrollTo({ top: this.#drawerScrollY, behavior: 'instant' });
      if (this.#previousScrollRestoration !== null) {
        history.scrollRestoration = this.#previousScrollRestoration;
        this.#previousScrollRestoration = null;
      }
      this.#closingFromHistory = false;
      this.#historyAbortController?.abort();
    });
  };

  /**
   * Announces updated cart counts when the shopper has opened the drawer.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #startStickyObservation = () => {
    this.#stopStickyObservation();
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    this.#summaryResizeObserver = new ResizeObserver(this.#scheduleStickyState);
    this.#summaryResizeObserver.observe(dialog);
    // Cart hydration may replace the summary node while the drawer stays open.
    this.#summaryMutationObserver = new MutationObserver(this.#scheduleStickyState);
    this.#summaryMutationObserver.observe(dialog, { childList: true, subtree: true });
    this.#updateStickyState();
  };

  #scheduleStickyState = () => {
    if (!this.refs.dialog?.open || this.#stickyFrame !== null) return;
    this.#stickyFrame = requestAnimationFrame(() => {
      this.#stickyFrame = null;
      this.#updateStickyState();
    });
  };

  #stopStickyObservation = () => {
    this.#summaryResizeObserver?.disconnect();
    this.#summaryMutationObserver?.disconnect();
    this.#summaryResizeObserver = null;
    this.#summaryMutationObserver = null;
    this.#observedSummary = null;
    if (this.#stickyFrame !== null) cancelAnimationFrame(this.#stickyFrame);
    this.#stickyFrame = null;
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog?.open) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (summary !== this.#observedSummary) {
      if (this.#observedSummary) this.#summaryResizeObserver?.unobserve(this.#observedSummary);
      if (summary) this.#summaryResizeObserver?.observe(summary);
      this.#observedSummary = summary;
    }

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    if (drawerHeight <= 0) return;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
